/**
 * customer-bootstrap · 纯函数工具
 *
 * 约束：
 *   ✓ 允许：localStorage 同步读、字符串/正则、URLSearchParams、setTimeout(focus)
 *   ✗ 禁止：fetch / WebSocket / setInterval 长任务 / 业务 DOM 写入
 */

import {
  PWD_LENGTH,
  STORAGE_KEYS,
  type NetworkConfig,
  type SystemSettings,
} from './types';

export const getQueryParam = (key: string): string | null => {
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch {
    return null;
  }
};

export const safeParse = <T>(raw: string | null | undefined): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const readLs = <T>(key: string): T | null => {
  try {
    return safeParse<T>(localStorage.getItem(key));
  } catch {
    return null;
  }
};

export const readSystemSettings = (): SystemSettings | null =>
  readLs<SystemSettings>(STORAGE_KEYS.systemSettings);

export const readNetworkConfig = (): NetworkConfig | null =>
  readLs<NetworkConfig>(STORAGE_KEYS.networkConfig);

export const isValidPin = (s: string): boolean => /^\d{6}$/.test(s);

/**
 * 把 6 个 input 拼成密码字符串，逐位剥离非数字。
 * 与密码闸 6 格输入行为一致。
 */
export const collectPin = (boxes: NodeListOf<HTMLInputElement> | HTMLInputElement[]): string => {
  let s = '';
  const arr = Array.from(boxes);
  for (const el of arr) {
    s += (el.value || '').replace(/\D/g, '').slice(0, 1);
  }
  return s.slice(0, PWD_LENGTH);
};

/**
 * iOS 12 安全 focus：
 *   - 优先 focus({ preventScroll }) 避免 iPad 抖屏
 *   - 不支持时回落原生 focus()
 *   - 全部包 try/catch，兜底失焦不抛错
 */
export const focusInput = (el: Element | HTMLElement | null, delayMs = 60): void => {
  if (!el) return;
  const target = el as HTMLElement;
  if (typeof (target as HTMLInputElement).focus !== 'function') return;
  setTimeout(() => {
    try {
      (target as HTMLInputElement).focus({ preventScroll: true } as FocusOptions);
    } catch {
      try {
        (target as HTMLInputElement).focus();
      } catch {
        /* noop */
      }
    }
  }, delayMs);
};

/** apiBaseUrl → ws(s)://host/ws；解析失败返回空串 */
export const apiBaseToWsUrl = (base: string): string => {
  const trimmed = String(base || '').replace(/\/$/, '');
  if (!trimmed) return '';
  try {
    const u = new URL(trimmed);
    const proto = u.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${u.host}/ws`;
  } catch {
    return '';
  }
};

/** matchMedia 兼容包装：iOS 12 支持，但 SSR / 测试环境可能没有 */
export const prefersDark = (): boolean => {
  try {
    return !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
};
